/**
 * BabyCareTimeline
 *
 * Displays a timeline of baby care entries grouped by day.
 * Includes filter tabs to filter by care type.
 */

import { useMemo, useState } from 'react';
import {
  Baby,
  Moon,
  Pill,
  Thermometer,
  StickyNote,
  Clock,
  Trash2,
  Droplets,
  Loader2,
  ChevronDown,
  Archive,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { BabyCareEntry, CareType } from '../index';

const CARE_LABEL_KEYS: Record<CareType, string> = {
  feeding: 'entryTypes.feeding',
  diaper: 'entryTypes.diaper',
  sleep: 'entryTypes.sleep',
  medication: 'entryTypes.medication',
  temperature: 'entryTypes.temperature',
  notes: 'entryTypes.note',
};

// Filter icons
const FILTER_ICONS: Record<CareType | 'all', React.ComponentType<{ className?: string }>> = {
  all: Archive,
  feeding: Baby,
  diaper: Droplets,
  sleep: Moon,
  medication: Pill,
  temperature: Thermometer,
  notes: StickyNote,
};

// Filter options
const FILTER_OPTIONS: { value: CareType | 'all'; labelKey: string }[] = [
  { value: 'all', labelKey: 'timeline.filterAll' },
  { value: 'feeding', labelKey: 'entryTypesShort.feeding' },
  { value: 'diaper', labelKey: 'entryTypesShort.diaper' },
  { value: 'sleep', labelKey: 'entryTypesShort.sleep' },
  { value: 'medication', labelKey: 'entryTypesShort.medication' },
  { value: 'temperature', labelKey: 'entryTypesShort.temperature' },
  { value: 'notes', labelKey: 'entryTypesShort.notes' },
];

interface BabyCareTimelineProps {
  entries: BabyCareEntry[];
  onDelete?: (id: string) => void;
  onEditSleep?: (entry: BabyCareEntry) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

// Get icon component for care type
function getIconComponent(type: CareType) {
  const icons = {
    feeding: Baby,
    diaper: Droplets,
    sleep: Moon,
    medication: Pill,
    temperature: Thermometer,
    notes: StickyNote,
  };
  return icons[type];
}

// Group entries by date
function groupByDate(entries: BabyCareEntry[]): Map<string, BabyCareEntry[]> {
  const groups = new Map<string, BabyCareEntry[]>();

  entries.forEach((entry) => {
    const date = new Date(entry.recorded_at).toLocaleDateString('de-DE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });

    const existing = groups.get(date) || [];
    groups.set(date, [...existing, entry]);
  });

  return groups;
}

// Format entry details
function formatEntryDetails(entry: BabyCareEntry, tFn: (key: string) => string): string {
  switch (entry.care_type) {
    case 'feeding':
      const feedingParts = [];
      if (entry.feeding_type === 'breast') {
        feedingParts.push(tFn('timeline.feedingDetail.breastfeeding'));
        if (entry.feeding_side) {
          feedingParts.push(entry.feeding_side === 'left' ? tFn('timeline.feedingDetail.left') : entry.feeding_side === 'right' ? tFn('timeline.feedingDetail.right') : tFn('timeline.feedingDetail.both'));
        }
      } else if (entry.feeding_type === 'bottle') {
        feedingParts.push(tFn('timeline.feedingDetail.bottle'));
        if (entry.feeding_amount_ml) {
          feedingParts.push(`${entry.feeding_amount_ml}ml`);
        }
      } else if (entry.feeding_type === 'solid') {
        feedingParts.push(tFn('timeline.feedingDetail.solidFood'));
      }
      if (entry.feeding_duration_minutes) {
        feedingParts.push(`${entry.feeding_duration_minutes} ${tFn('timeline.minutes')}`);
      }
      return feedingParts.join(' · ');

    case 'diaper':
      if (entry.diaper_type === 'wet') return tFn('timeline.diaperDetail.wet');
      if (entry.diaper_type === 'dirty') return tFn('timeline.diaperDetail.soiled');
      if (entry.diaper_type === 'both') return tFn('timeline.diaperDetail.wetAndSoiled');
      return tFn('timeline.diaperDetail.dry');

    case 'sleep':
      const sleepParts = [];
      if (entry.ended_at) {
        const start = new Date(entry.recorded_at);
        const end = new Date(entry.ended_at);
        const durationMinutes = Math.floor((end.getTime() - start.getTime()) / 1000 / 60);
        const hours = Math.floor(durationMinutes / 60);
        const mins = durationMinutes % 60;
        if (hours > 0) {
          sleepParts.push(`${hours}h ${mins}min`);
        } else {
          sleepParts.push(`${mins} ${tFn('timeline.minutes')}`);
        }
        if (entry.sleep_quality) {
          sleepParts.push(entry.sleep_quality === 'good' ? tFn('timeline.sleepDetail.good') : entry.sleep_quality === 'okay' ? tFn('timeline.sleepDetail.okay') : tFn('timeline.sleepDetail.bad'));
        }
      } else {
        sleepParts.push(tFn('timeline.sleepDetail.running'));
      }
      return sleepParts.join(' · ');

    case 'medication':
      const medParts = [];
      if (entry.medication_name) medParts.push(entry.medication_name);
      if (entry.medication_dose) medParts.push(entry.medication_dose);
      return medParts.join(' · ') || tFn('entryTypes.medication');

    case 'temperature':
      const tempParts = [];
      if (entry.temperature_value) tempParts.push(`${entry.temperature_value}°C`);
      if (entry.temperature_location) {
        const locations: Record<string, string> = {
          ear: tFn('timeline.temperatureDetail.ear'),
          forehead: tFn('timeline.temperatureDetail.forehead'),
          armpit: tFn('timeline.temperatureDetail.armpit'),
          rectal: tFn('timeline.temperatureDetail.rectal'),
        };
        tempParts.push(locations[entry.temperature_location] || entry.temperature_location);
      }
      return tempParts.join(' · ');

    case 'notes':
      return entry.notes || tFn('entryTypes.note');

    default:
      return '';
  }
}

export function BabyCareTimeline({ entries, onDelete, onEditSleep, hasMore, isLoadingMore, onLoadMore }: BabyCareTimelineProps) {
  const { t } = useTranslation('babyCare');
  const [filter, setFilter] = useState<CareType | 'all'>('all');

  // Filter entries based on selected filter
  const filteredEntries = useMemo(() => {
    if (filter === 'all') return entries;
    return entries.filter(entry => entry.care_type === filter);
  }, [entries, filter]);

  const groupedEntries = useMemo(() => groupByDate(filteredEntries), [filteredEntries]);

  // Get available filters (only show filters for care types that have entries)
  const availableFilters = useMemo(() => {
    const typesWithEntries = new Set(entries.map(e => e.care_type));
    return FILTER_OPTIONS.filter(
      (opt: { value: CareType | 'all'; labelKey: string }) => opt.value === 'all' || typesWithEntries.has(opt.value as CareType)
    );
  }, [entries]);

  if (entries.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
          <Clock className="w-8 h-8 text-white/40" />
        </div>
        <p className="text-white/60">{t('timeline.empty')}</p>
        <p className="text-white/40 text-sm mt-1">
          {t('timeline.emptyHint')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Tabs - horizontally scrollable, DevelopmentTabs style */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {availableFilters.map((option) => {
          const FilterIcon = FILTER_ICONS[option.value];
          const isActive = filter === option.value;

          return (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all whitespace-nowrap border ${
                isActive
                  ? 'bg-pp-orange border-pp-orange text-white'
                  : 'bg-[rgba(31,41,55,0.35)] backdrop-blur-[5px] border-white/25 text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              <FilterIcon className="w-5 h-5" />
              {isActive && (
                <span className="text-sm font-medium">
                  {t(option.labelKey)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      {filteredEntries.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-white/60">{t('timeline.noFilteredEntries')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Array.from(groupedEntries.entries()).map(([date, dayEntries]) => (
            <div key={date}>
              {/* Date Header */}
              <div className="sticky top-0 bg-pp-dark-blue/95 backdrop-blur py-2 z-10">
                <h3 className="text-white/70 text-sm font-medium">{date}</h3>
              </div>

              {/* Entries */}
              <div className="mt-2">
                {dayEntries.map((entry, index) => {
                  const IconComponent = getIconComponent(entry.care_type);

                  // Completed sleep entries are editable via tap (running timers aren't, they're managed by the ActiveSleepTimerCard)
                  const isEditableSleep =
                    !!onEditSleep && entry.care_type === 'sleep' && entry.ended_at !== null;
                  const rowClickHandler = isEditableSleep ? () => onEditSleep!(entry) : undefined;

                  return (
                    <div key={entry.id}>
                      <div
                        className={`py-3 flex items-start gap-3 ${
                          isEditableSleep
                            ? '-mx-2 px-2 rounded-[10px] cursor-pointer hover:bg-white/5 transition-colors'
                            : ''
                        }`}
                        onClick={rowClickHandler}
                        role={isEditableSleep ? 'button' : undefined}
                        tabIndex={isEditableSleep ? 0 : undefined}
                        onKeyDown={
                          isEditableSleep
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onEditSleep!(entry);
                                }
                              }
                            : undefined
                        }
                      >
                        {/* Icon */}
                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 text-white/25" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-white font-medium">
                              {t(CARE_LABEL_KEYS[entry.care_type])}
                            </span>
                            <span className="text-white/50 text-sm">
                              {new Date(entry.recorded_at).toLocaleTimeString('de-DE', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                          <p className="text-white/70 text-sm mt-0.5">
                            {formatEntryDetails(entry, t)}
                          </p>
                          {entry.notes && entry.care_type !== 'notes' && (
                            <p className="text-white/50 text-xs mt-1 italic truncate">
                              {entry.notes}
                            </p>
                          )}
                        </div>

                        {/* Delete Button */}
                        {onDelete && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(entry.id);
                            }}
                            className="p-2 hover:bg-white/10 rounded-full transition-colors flex-shrink-0"
                            title={t('timeline.delete')}
                          >
                            <Trash2 className="w-4 h-4 text-white/40 hover:text-red-400" />
                          </button>
                        )}
                      </div>
                      {index < dayEntries.length - 1 && (
                        <div className="flex justify-center">
                          <div className="w-full h-px bg-white/25" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {hasMore && onLoadMore && filter === 'all' && (
            <div className="flex justify-center pt-2">
              <button
                onClick={onLoadMore}
                disabled={isLoadingMore}
                className="py-2.5 px-6 bg-pp-orange rounded-[10px] text-white font-medium hover:bg-pp-orange/90 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{t('timeline.loading')}</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    <span>{t('timeline.loadMore')}</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
